import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Upload, Trash2, Lock, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const StoragePhotosPanel = ({ month, year, authHeaders, storageAreas }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previews, setPreviews] = useState({}); // photoId -> blob url
  const [uploadingArea, setUploadingArea] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [lightboxPhotoId, setLightboxPhotoId] = useState(null);
  const [approval, setApproval] = useState({ approved: false, approvedBy: null, approvedAt: null });
  const fileInputs = useRef({});

  const areas = storageAreas || [];
  const approved = approval.approved;

  const fetchPhotos = useCallback(async () => {
    setLoading(true);
    try {
      const [photosRes, statusRes] = await Promise.all([
        axios.get(`${API}/inventory/storage-photos`, { params: { month, year }, headers: authHeaders }),
        axios.get(`${API}/inventory/take-inventory`, { params: { month, year }, headers: authHeaders }),
      ]);
      setPhotos(photosRes.data.photos || []);
      setApproval({
        approved: !!statusRes.data.approved,
        approvedBy: statusRes.data.approvedBy,
        approvedAt: statusRes.data.approvedAt,
      });
    } catch (err) {
      console.error("Error fetching storage photos:", err);
    } finally {
      setLoading(false);
    }
  }, [month, year, authHeaders]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const loadPreview = useCallback(async (photoId) => {
    if (previews[photoId]) return;
    try {
      const res = await axios.get(`${API}/inventory/storage-photos/${photoId}/download`, {
        headers: authHeaders, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      setPreviews((p) => ({ ...p, [photoId]: url }));
    } catch (err) {
      console.error("Error loading photo preview:", err);
    }
  }, [authHeaders, previews]);

  useEffect(() => {
    photos.forEach((p) => loadPreview(p.id));
  }, [photos, loadPreview]);

  const handleFileSelected = async (storageAreaKey, file) => {
    if (!file) return;
    setUploadingArea(storageAreaKey);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("month", month);
      formData.append("year", year);
      formData.append("storageArea", storageAreaKey);
      await axios.post(`${API}/inventory/storage-photos/upload`, formData, {
        headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo added");
      fetchPhotos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploadingArea(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/inventory/storage-photos/${deleteTarget}`, { headers: authHeaders });
      toast.success("Photo removed");
      setDeleteTarget(null);
      fetchPhotos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove photo");
    }
  };

  if (loading) {
    return <p className="text-sm text-slate-400 py-6 text-center">Loading photos...</p>;
  }

  return (
    <div data-testid="storage-photos-panel" className="space-y-6">
      <p className="text-sm text-slate-500">
        Optional photos of each storage area for this month — just for reference during Review &amp; Approve.
        Counts stay 100% manual; nothing here is analyzed or auto-changes any count.
      </p>

      {approved && (
        <div data-testid="storage-photos-locked-banner" className="flex items-center gap-2 text-sm bg-slate-100 border border-slate-200 rounded-lg px-3 py-2 text-slate-600">
          <Lock className="w-4 h-4" /> This month was approved by {approval.approvedBy} on{" "}
          {approval.approvedAt ? new Date(approval.approvedAt).toLocaleDateString() : ""} — photos are locked.
        </div>
      )}

      {areas.map((area) => {
        const areaPhotos = photos.filter((p) => p.storageArea === area.key);
        return (
          <div key={area.key} className="border border-slate-200 rounded-xl p-4" data-testid={`storage-photos-area-${area.key}`}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-slate-800">{area.label} ({areaPhotos.length})</h4>
              {!approved && (
                <div className="flex gap-2">
                  <input
                    type="file" accept="image/*" capture="environment" className="hidden"
                    ref={(el) => (fileInputs.current[`${area.key}-camera`] = el)}
                    onChange={(e) => handleFileSelected(area.key, e.target.files?.[0])}
                  />
                  <input
                    type="file" accept="image/*" className="hidden"
                    ref={(el) => (fileInputs.current[`${area.key}-upload`] = el)}
                    onChange={(e) => handleFileSelected(area.key, e.target.files?.[0])}
                  />
                  <Button size="sm" variant="outline" data-testid={`take-photo-${area.key}`}
                    disabled={uploadingArea === area.key}
                    onClick={() => fileInputs.current[`${area.key}-camera`]?.click()}>
                    <Camera className="w-4 h-4 mr-1" /> Take Photo
                  </Button>
                  <Button size="sm" variant="outline" data-testid={`upload-photo-${area.key}`}
                    disabled={uploadingArea === area.key}
                    onClick={() => fileInputs.current[`${area.key}-upload`]?.click()}>
                    <Upload className="w-4 h-4 mr-1" /> Upload
                  </Button>
                </div>
              )}
            </div>

            {areaPhotos.length === 0 ? (
              <p className="text-xs text-slate-400">No photos added yet for {area.label}.</p>
            ) : (
              <div className="flex flex-wrap gap-3">
                {areaPhotos.map((photo) => (
                  <div key={photo.id} data-testid={`storage-photo-thumb-${photo.id}`} className="relative group">
                    <button type="button" onClick={() => setLightboxPhotoId(photo.id)} className="block">
                      {previews[photo.id] ? (
                        <img src={previews[photo.id]} alt={photo.originalFilename}
                          className="w-24 h-24 object-cover rounded-lg border border-slate-200" />
                      ) : (
                        <div className="w-24 h-24 rounded-lg border border-slate-200 bg-slate-50 animate-pulse" />
                      )}
                    </button>
                    {!approved && (
                      <button type="button" data-testid={`delete-photo-${photo.id}`}
                        onClick={() => setDeleteTarget(photo.id)}
                        className="absolute -top-2 -right-2 bg-white border border-slate-200 rounded-full p-1 text-rose-500 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {lightboxPhotoId && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={() => setLightboxPhotoId(null)}>
          <button type="button" className="absolute top-4 right-4 text-white" onClick={() => setLightboxPhotoId(null)} data-testid="close-photo-lightbox">
            <X className="w-6 h-6" />
          </button>
          {previews[lightboxPhotoId] && (
            <img src={previews[lightboxPhotoId]} alt="Storage area" className="max-h-[85vh] max-w-full rounded-lg" onClick={(e) => e.stopPropagation()} />
          )}
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-photo-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this photo?</AlertDialogTitle>
            <AlertDialogDescription>This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-photo-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-photo-confirm" onClick={handleDelete}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StoragePhotosPanel;
